import { cn } from '@lib/utils';

/**
 * Witzy brand assets. The reference PNG ships from
 * `apps/frontend/public/logo-witzy-04.png` which Vite serves at
 * `/logo-witzy-04.png`. We expose two wrappers:
 *
 *  - `<WitzyMark>` — the glyph alone (the circular W). Used in the
 *    collapsed sidebar and anywhere we have square real estate.
 *  - `<WitzyLogo>` — the full lock-up. The supplied PNG already
 *    contains the glyph and the "witzy WA" wordmark together,
 *    so this component renders the same image at a larger width.
 *
 * Both are rendered as <img> so the file ships once and the browser
 * decodes it on a worker thread. We rely on the asset already being
 * tightly cropped — no extra wordmark glyph is drawn alongside it.
 *
 * NOTE: keep the old `logo-witzy-03-new.png` file in public/ — emails
 * already delivered reference it by URL and would break if removed.
 */

const LOGO_SRC = '/logo-witzy-04.png';

export interface WitzyMarkProps {
  /** Rendered width in px (height auto). Defaults to 32. */
  size?: number;
  className?: string;
  /**
   * `tone="white"` flattens the artwork to solid white via a CSS filter so
   * it reads on a coloured background.
   *
   * Only safe for single-colour usage: the current lock-up carries a green
   * "WA" badge with white text inside it, and flattening collapses badge and
   * text into one blank rectangle. On coloured backgrounds prefer putting the
   * natural mark on a light chip (see `AuthLayout`).
   */
  tone?: 'navy' | 'white';
}

const TONE_FILTER: Record<NonNullable<WitzyMarkProps['tone']>, string> = {
  navy: 'none',
  white: 'brightness(0) invert(1)',
};

export function WitzyMark({
  size = 32,
  className,
  tone = 'navy',
}: WitzyMarkProps): JSX.Element {
  return (
    <img
      src={LOGO_SRC}
      alt="Witzy"
      width={size}
      height={size}
      // The supplied PNG is a wide lock-up. Crop to the leftmost
      // square via `object-position` so the glyph is what's shown when
      // the caller asks for a square mark (sidebar collapsed state).
      className={cn('block object-contain object-left', className)}
      style={{
        height: size,
        width: size,
        filter: TONE_FILTER[tone],
      }}
      draggable={false}
    />
  );
}

export interface WitzyLogoProps {
  /** Wordmark text size in px — the image scales proportionally. */
  textSize?: number;
  className?: string;
  tone?: 'navy' | 'white';
}

export function WitzyLogo({
  textSize = 22,
  className,
  tone = 'navy',
}: WitzyLogoProps): JSX.Element {
  // The PNG aspect ratio is roughly 4:1 (mark + wordmark). Height
  // matches the requested text size so the lock-up sits on the same
  // baseline as adjacent text; width is `textSize * 4` to honour the
  // source proportions.
  const height = Math.round(textSize * 1.4);
  return (
    <img
      src={LOGO_SRC}
      alt="witzy.ai"
      className={cn('block object-contain', className)}
      style={{
        height,
        width: 'auto',
        filter: TONE_FILTER[tone],
      }}
      draggable={false}
    />
  );
}
